/**
 * Who is looking at Firm Knowledge and for which firm. Members read; who
 * writes the library and skills is the firm's choice (Settings > Permissions),
 * so the server says what this user may do. A superadmin can act for another
 * firm via `?law_firm_id=`, which every API call then carries.
 */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import type { FirmScope } from "@/api/firm-knowledge";
import { LawFirmsAPI } from "@/api/lawFirms";
import type { FirmCapability } from "@/api/types/lawFirm";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";

export type FirmKnowledgeScope = {
  scope: FirmScope;
  /** The firm whose knowledge is on screen (own firm unless overridden). */
  lawFirmId: string | null;
  /** May write skills (the firm's permissions; superadmins always). */
  canEdit: boolean;
  /** May add to and edit the library (the firm's permissions; superadmins always). */
  canEditLibrary: boolean;
  /** The permissions above have loaded (until then both read false). */
  permissionsReady: boolean;
  isSuperAdmin: boolean;
  lawyerId: string | undefined;
};

type Mine = Record<FirmCapability, boolean>;

const NONE: Mine = { library_write: false, skills_write: false };

export const useFirmScope = (): FirmKnowledgeScope => {
  const lawyer = useSelector(lawyerSelectors.selectAll);
  const [searchParams] = useSearchParams();
  const override = searchParams.get("law_firm_id");

  const role = lawyer?.role as string | undefined;
  const isSuperAdmin = role === "superadmin";
  const ownFirm =
    (lawyer as { law_firm_id?: string | null })?.law_firm_id ?? null;
  const lawFirmId = isSuperAdmin && override ? override : ownFirm;

  const [mine, setMine] = useState<{ firm: string | null; can: Mine }>({
    firm: null,
    can: NONE
  });

  useEffect(() => {
    if (!lawFirmId || isSuperAdmin) return undefined;
    let cancelled = false;
    LawFirmsAPI.getPermissions(lawFirmId)
      .then((data) => {
        if (!cancelled) setMine({ firm: lawFirmId, can: data.mine });
      })
      .catch(() => {
        if (!cancelled) setMine({ firm: lawFirmId, can: NONE });
      });
    return () => {
      cancelled = true;
    };
  }, [lawFirmId, isSuperAdmin]);

  return useMemo(() => {
    const can = isSuperAdmin
      ? { library_write: true, skills_write: true }
      : mine.firm === lawFirmId
      ? mine.can
      : NONE;
    return {
      scope: isSuperAdmin && override ? { lawFirmId: override } : {},
      lawFirmId,
      canEdit: can.skills_write,
      canEditLibrary: can.library_write,
      permissionsReady: isSuperAdmin || !lawFirmId || mine.firm === lawFirmId,
      isSuperAdmin,
      lawyerId: lawyer?.uid as string | undefined
    };
  }, [lawyer, override, lawFirmId, isSuperAdmin, mine]);
};
